import { Create, Delete, Edit, Warning } from '@mui/icons-material'
import {
	Box,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	TableCell,
	Typography,
} from '@mui/material'
import { observer, useLocalObservable } from 'mobx-react'
import { useEffect, useMemo } from 'react'
import { useHistory } from 'react-router'
import {
	BaseEventListenerModel,
	EventHandler,
	EventListenerMetadata,
} from '../../../../api/clients/events/DTO'
import EventHandlersClient from '../../../../api/clients/events/EventHandlersClient'
import EventListenersClient from '../../../../api/clients/events/EventListenersClient'
import EventsClient from '../../../../api/clients/events/EventsClient'
import { FullscreenSpinner } from '../../../../components/feedback/circular'
import { TableColumn } from '../../../../components/tables/DTO'
import { FilteredTable } from '../../../../components/tables/FilteredTable'
import {
	FilterControl,
	SelectFilterOption,
} from '../../../../components/tables/filters/DTO'
import { useModals } from '../../../../services/notifications/ModalService'
import { usePrompts } from '../../../../services/notifications/PromptService'
import { toastService } from '../../../../services/notifications/ToastService'
import AdministrationPageContainer from '../AdministrationPageContainer'
import { CreateEventListenerDialog } from './CreateEventListenerDialog'

export const EventListenersDashboard = observer(() => {
	const history = useHistory()
	const modalService = useModals()
	const promptService = usePrompts()

	const localStore = useLocalObservable(() => ({
		eventListeners: undefined as EventListenerMetadata[] | undefined,
		eventHandlers: [] as EventHandler[],
		eventIds: [] as string[],
	}))

	const eventListenersClient = new EventListenersClient()
	const eventHandlersClient = new EventHandlersClient()
	const eventsClient = new EventsClient()

	useEffect(() => {
		eventListenersClient.getEventListenerMetadata().then((response) => {
			localStore.eventListeners = response.data
		})

		eventHandlersClient.getEventHandlers().then((response) => {
			localStore.eventHandlers = response.data
		})

		eventsClient.getEvents().then((response) => {
			localStore.eventIds = response.data
		})
	}, [])

	const handleEditEventListener = (eventListenerId: number) => {
		history.push(`_event-listeners/${eventListenerId}`)
	}

	const handleDeleteEventListener = async (
		eventListener: EventListenerMetadata,
	) => {
		if (localStore.eventListeners === undefined) return

		try {
			await eventListenersClient.deleteEventListener(eventListener.id)

			toastService.displayToast({
				message: 'Event listener deleted',
				area: 'global',
				severity: 'success',
			})

			localStore.eventListeners = localStore.eventListeners.filter(
				(v) => v.id !== eventListener.id,
			)
		} catch (e) {
			console.error(e)
			toastService.displayToast({
				message: 'Error deleting event listener',
				area: 'global',
				severity: 'error',
			})
		}
	}

	const handleCreateEventListenerClicked = () => {
		modalService
			.showForm((props) => (
				<CreateEventListenerDialog
					eventHandlers={localStore.eventHandlers}
					events={localStore.eventIds}
					existingEventListenerNames={
						localStore.eventListeners?.map((v) => v.displayName) ?? []
					}
					onConfirm={(v) => props.close({ closeResult: 'okay', value: v })}
					onCancel={() => props.close({ closeResult: 'cancel' })}
				/>
			))
			.then(async (v) => {
				if (v.closeResult === 'cancel') return

				try {
					const { data: newEventListener } =
						await eventListenersClient.createEventListener(
							v.value as BaseEventListenerModel,
						)

					toastService.displayToast({
						message: 'Event listener created',
						area: 'global',
						severity: 'success',
					})

					history.push(`_event-listeners/${newEventListener.id}`)
				} catch (e) {
					console.error(e)

					toastService.displayToast({
						message: 'Error creating event listener',
						area: 'global',
						severity: 'error',
					})
				}
			})
	}

	const getTypeOptions = useMemo((): SelectFilterOption[] => {
		const typeOptions: Record<string, unknown> = {}
		localStore.eventListeners?.forEach(
			(e) => (typeOptions[e.eventHandlerType] = true),
		)
		return Object.keys(typeOptions).map((k) => ({
			label: k,
			value: k,
		}))
	}, [localStore.eventListeners])

	const getEventOptions = useMemo((): SelectFilterOption[] => {
		const eventOptions: Record<string, unknown> = {}
		localStore.eventListeners?.forEach((e) => (eventOptions[e.eventId] = true))
		return (
			[{ label: 'All Events', value: -1 }] as SelectFilterOption[]
		).concat(
			Object.keys(eventOptions).map((k) => ({
				label: k,
				value: k,
			})),
		)
	}, [localStore.eventListeners])

	const filterControls: FilterControl[] = useMemo(
		() => [
			{
				type: 'multiselect',
				name: 'eventHandlerType',
				label: 'Event Handler',
				options: getTypeOptions,
			},
			{
				type: 'select',
				name: 'eventId',
				column: ['name', 'description'],
				options: getEventOptions,
				filterEntry: (entry, filterValue) =>
					filterValue === -1
						? true
						: filterValue === (entry as EventListenerMetadata).eventId,
			},
			{
				type: 'search',
				name: 'search',
				column: ['displayName', 'description'],
			},
		],
		[localStore.eventListeners],
	)

	const columns: TableColumn<EventListenerMetadata>[] = useMemo(
		() => [
			{
				name: 'displayName',
				label: 'Name',
				renderContent: (e) => (
					<Typography
						variant="inherit"
						color={e.isMissingPlugin ? 'error' : ''}
					>
						{e.displayName}
					</Typography>
				),
			},
			{
				name: 'eventId',
				label: 'Event',
			},
			{
				name: 'eventHandlerType',
				label: 'Event Handler',
				renderContent: (e) =>
					e.isMissingPlugin ? (
						<Box display="flex" alignItems="center" gap={1}>
							<Warning color="error" fontSize="small" />
							<Typography variant="subtitle2" color="error">
								Plugin Missing
							</Typography>
						</Box>
					) : (
						e.eventHandlerType
					),
			},
			{
				name: 'description',
				label: 'Description',
				renderContent: (e) =>
					e.description.length < 100
						? e.description
						: e.description.substring(0, 99) + '...',
			},
			{
				renderHeading: (
					<TableCell key={4} align="right">
						Actions
					</TableCell>
				),
				renderCell: (e) => (
					<TableCell key={4} align="right">
						{!e.isMissingPlugin && (
							<IconButton onClick={() => handleEditEventListener(e.id)}>
								<Edit />
							</IconButton>
						)}
						<IconButton
							size="small"
							onClick={() => {
								promptService
									.showDialog((props) => (
										<ConfirmDeleteEventListenerPrompt
											eventListenerName={e.displayName}
											onCancel={() => props.close('cancel')}
											onConfirm={() => props.close('okay')}
										/>
									))
									.then((v) => {
										if (v === 'okay') handleDeleteEventListener(e)
									})
							}}
						>
							<Delete />
						</IconButton>
					</TableCell>
				),
			},
		],
		[],
	)

	return (
		<AdministrationPageContainer
			title="Event Listeners"
			actions={
				<div>
					<Button
						variant="contained"
						hidden={localStore.eventHandlers.length === 0}
						startIcon={<Create />}
						color="primary"
						onClick={handleCreateEventListenerClicked}
					>
						Create Event Listener
					</Button>
				</div>
			}
		>
			{localStore.eventListeners === undefined ? (
				<FullscreenSpinner />
			) : (
				<FilteredTable
					allEntries={localStore.eventListeners}
					columns={columns}
					filterControls={filterControls}
				/>
			)}
		</AdministrationPageContainer>
	)
})

type ConfirmDeleteEventListenerPromptProps = {
	eventListenerName: string
	onConfirm: () => void
	onCancel: () => void
}

const ConfirmDeleteEventListenerPrompt = (
	props: ConfirmDeleteEventListenerPromptProps,
) => {
	return (
		<>
			<DialogTitle>Confirm Delete Event Listener</DialogTitle>
			<DialogContent>
				<Typography>
					Are you sure that you want to delete the{' '}
					<b>{props.eventListenerName}</b> event listener?
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button color="primary" onClick={props.onCancel}>
					Cancel
				</Button>
				<Button color="primary" variant="contained" onClick={props.onConfirm}>
					Confirm
				</Button>
			</DialogActions>
		</>
	)
}
